/**
 * Dashboard.jsx
 * What's going out, how it did, what needs you, and whether the plumbing works.
 *
 * The screen is assembled from cards in components/dashboard/, each of which
 * owns one question. This file's job is the layout, the one request that feeds
 * it, and the window control that scopes everything below it.
 *
 * Everything shown is measured. Where a figure has not been collected the card
 * says so rather than drawing a zero — see components/dashboard/helpers.js.
 */

import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api.js';
import { useBrands } from '../hooks/useBrands.jsx';
import { useSession } from '../hooks/useSession.jsx';
import { Plus, Sparkle, Warning } from '../components/Icons.jsx';
import ConnectionNotice from '../components/ConnectionNotice.jsx';

import StatRow from '../components/dashboard/StatRow.jsx';
import PerformanceChart from '../components/dashboard/PerformanceChart.jsx';
import BrandBreakdown from '../components/dashboard/BrandBreakdown.jsx';
import ChartScope, { scopeLabel, scoped } from '../components/dashboard/ChartScope.jsx';
import UpcomingPosts, { ApprovalQueue } from '../components/dashboard/PostList.jsx';
import BrandHealth from '../components/dashboard/BrandHealth.jsx';
import Insights from '../components/dashboard/Insights.jsx';
import RecentAssets, { AiAssistant } from '../components/dashboard/RecentAssets.jsx';
import NeedsAttention, { SystemStatus } from '../components/dashboard/NeedsAttention.jsx';
import { greeting } from '../components/dashboard/helpers.js';

const WINDOWS = [7, 30, 90];

/** Nothing has loaded yet — the shape of the payload, with every figure absent. */
const EMPTY = {
  upcoming: [],
  failed: [],
  queue: [],
  assets: [],
  hashtags: [],
  competitors: [],
  drafts: 0,
  measuredAt: null,
  stats: { scheduled: 0, nextOut: null, brands: 0, connectedBrands: 0, spend: {} },
  performance: { current: {}, previous: {}, series: [], breakdown: [], seriesBy: [] },
};

export default function Dashboard() {
  const navigate = useNavigate();
  const { brands, refresh: refreshBrands } = useBrands();
  const { user, apiUp, dbOk, error: sessionError, refresh: refreshSession, checking } = useSession();

  const [days, setDays] = useState(30);
  // What the performance chart is narrowed to. The totals above it and the
  // breakdown below always show everything; only the chart is scoped.
  const [chartBrand, setChartBrand] = useState('all');
  const [chartPlatform, setChartPlatform] = useState('all');
  const [data, setData] = useState(EMPTY);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await api.dashboard({ days }));
    } catch {
      setData(EMPTY);
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    load();
  }, [load]);

  const refreshAll = () => {
    refreshSession();
    refreshBrands();
    load();
  };

  const connected = brands.filter((b) =>
    b.accounts?.some((a) => a.status === 'connected' || a.status === 'expiring'),
  ).length;

  // Brands whose accounts were connected and have since stopped working. This
  // is the state that quietly stops everything publishing, so it stays above
  // everything rather than being discovered on the day a post was due.
  const brokenBrands = brands.filter((b) =>
    b.accounts?.some((a) => a.status !== 'connected' && a.status !== 'expiring'),
  );

  const chart = scoped(data.performance, chartBrand, chartPlatform);

  return (
    <>
      <div className="page-head page-head--hero">
        <div>
          <div className="page-head__eyebrow">Dashboard</div>
          <h1>
            {greeting()}, <span className="accent-text">KreeBoom</span>
          </h1>
          <p>Here’s what’s happening across your brands today.</p>
        </div>

        <div className="page-head__actions">
          {/* One filter row, above everything it scopes: the figures, the
              chart and the comparisons all read the same window. */}
          <div className="segmented" role="group" aria-label="Time window">
            {WINDOWS.map((d) => (
              <button
                key={d}
                type="button"
                className={days === d ? 'is-active' : ''}
                aria-pressed={days === d}
                onClick={() => setDays(d)}
              >
                {d} days
              </button>
            ))}
          </div>

          <button className="btn" onClick={() => navigate('/create')}>
            <Sparkle size={16} /> Generate
          </button>
          <button className="btn btn--primary" onClick={() => navigate('/create')}>
            <Plus /> Create post
          </button>
        </div>
      </div>

      <SystemStatus
        apiUp={apiUp}
        dbOk={dbOk}
        user={user}
        brands={brands.length}
        connected={connected}
        checking={checking}
        onRefresh={refreshAll}
      />

      {sessionError && (
        <div className="notice notice--error">
          <Warning />
          <span>
            Can’t reach the API: {sessionError.message}. If you’re running locally, check the
            Worker is up.
          </span>
        </div>
      )}

      {brokenBrands.map((brand) => (
        <ConnectionNotice key={brand.id} brand={brand} />
      ))}

      <StatRow data={data} days={days} onNavigate={navigate} />

      <div className="dash-grid dash-grid--main">
        <UpcomingPosts posts={data.upcoming} navigate={navigate} />
        <PerformanceChart
          series={chart.series}
          current={chart.current}
          days={days}
          measuredAt={data.measuredAt}
          loading={loading}
          scopeLabel={scopeLabel(brands, chartBrand, chartPlatform)}
          filter={
            <ChartScope
              brands={brands}
              brandId={chartBrand}
              platform={chartPlatform}
              onBrand={setChartBrand}
              onPlatform={setChartPlatform}
            />
          }
        />
        {/* Both of these are short, so they share the third column rather
            than each stretching to the chart's height. */}
        <div className="dash-col">
          <BrandHealth brands={brands} navigate={navigate} />
          <Insights hashtags={data.hashtags} competitors={data.competitors} navigate={navigate} />
        </div>
      </div>

      {/* Which brand, on which platform, earned it. Full width, because it is
          a table and a table squeezed into a third of the screen is unreadable. */}
      <BrandBreakdown
        rows={data.performance?.breakdown ?? []}
        days={days}
        loading={loading}
      />

      <div className="dash-grid dash-grid--secondary">
        <ApprovalQueue posts={data.queue} total={data.drafts} navigate={navigate} />
        <AiAssistant navigate={navigate} />
        <RecentAssets assets={data.assets} navigate={navigate} />
      </div>

      <NeedsAttention failed={data.failed} onChanged={load} navigate={navigate} />
    </>
  );
}
